// Every lyric save is a revision; the lossy 'fix' path is retired, so there's
// no longer a structural-vs-text distinction to detect. Always 'arrange'.
// ('compose' is forced separately for the full-text editor.)
export function detectEditType() {
  return 'arrange'
}

export function splitLineAt(line, cursorIndex, makeIdentifier) {
  const text = line.line || ''
  const firstText = text.slice(0, cursorIndex)
  const secondRaw = text.slice(cursorIndex)
  const secondText = secondRaw.charAt(0).toUpperCase() + secondRaw.slice(1)
  const totalLen = Math.max(text.length, 1)
  const firstLen = Math.max(firstText.length, 1)
  const duration = line.t_end - line.t_start
  const firstDuration = Math.round((duration * firstLen) / totalLen)
  const splitTime = line.t_start + firstDuration

  const first = {
    ...line,
    line: firstText,
    t_start: line.t_start,
    t_end: splitTime,
    start_adj: line.start_adj !== undefined ? line.start_adj : 0,
    end_adj: 0,
  }
  const second = {
    ...line,
    identifier: makeIdentifier(),
    isNew: true,
    line: secondText,
    t_start: splitTime,
    t_end: line.t_end,
    start_adj: 0,
    end_adj: line.end_adj !== undefined ? line.end_adj : 0,
  }
  return { first, second }
}

export function gapTimingForInsert(prevLine, nextLine, videoDuration) {
  const t_start = prevLine ? prevLine.t_end : 0
  const t_end = nextLine ? nextLine.t_start : videoDuration
  return { t_start, t_end }
}

export function validateLyricsForSave(lyrics, currentVideoOffset) {
  for (let i = 0; i < lyrics.length; i++) {
    const l = lyrics[i]
    if (l.isDeleted) continue
    if (l.isNew && (!l.line || l.line.trim() === '')) {
      return `Line ${i + 1} has empty text. Type some text or delete the line before saving.`
    }
    if (l.line === '') continue
    const startTime =
      currentVideoOffset + l.t_start + (l.start_adj !== undefined ? l.start_adj : 0)
    const endTime =
      currentVideoOffset + l.t_end + (l.end_adj !== undefined ? l.end_adj : 0)
    if (startTime > endTime) {
      return `Invalid timing detected on Line ${i + 1}: "${l.line}" has a start time (${(startTime / 1000).toFixed(2)}s) that occurs after its end time (${(endTime / 1000).toFixed(2)}s). Please adjust the timing values so that the start time comes before the end time.`
    }
  }
  return null
}

export function hasLyricListChanges(originalLyrics, currentLyrics) {
  const liveCurrent = currentLyrics.filter(l => !l.isDeleted)
  if (liveCurrent.length !== originalLyrics.length) return true
  for (let i = 0; i < liveCurrent.length; i++) {
    if (liveCurrent[i].identifier !== originalLyrics[i].identifier) return true
    if (liveCurrent[i].line !== originalLyrics[i].line) return true
  }
  return false
}

export function findIndexById(arr, identifier) {
  return arr.findIndex(l => l.identifier === identifier)
}

// Predicate for "the editor can see this line right now".
// True for non-deleted lines that have text OR are freshly inserted (isNew + empty,
// awaiting the editor's first keystroke). False for legacy blank separators and
// soft-deleted rows.
export function isVisibleLine(line) {
  return !line.isDeleted && (line.line !== '' || !!line.isNew)
}

// Both find* helpers take `startExclusive`: searches stop before that index.
// Symmetric so call sites can pass the SAME index for both:
//   findPrevVisible(arr, i) → first visible line strictly before index i
//   findNextVisible(arr, i) → first visible line at-or-after index i
function findPrevVisibleImpl(arr, startExclusive) {
  for (let i = startExclusive - 1; i >= 0; i--) {
    if (isVisibleLine(arr[i])) return arr[i]
  }
  return null
}

function findNextVisibleImpl(arr, startInclusive) {
  for (let i = startInclusive; i < arr.length; i++) {
    if (isVisibleLine(arr[i])) return arr[i]
  }
  return null
}

export const findPrevVisible = findPrevVisibleImpl
export const findNextVisible = findNextVisibleImpl
